import { Download, Printer } from "lucide-react";
import { RESUME } from "@/data/resume";
import { play } from "@/lib/sound";
import { useAchievements } from "@/os/state/achievements";

export function ResumePanel() {
    const { trackEgg } = useAchievements();
    return (
        <div data-testid="resume-panel">
            <div className="no-print mb-4 flex flex-wrap items-center gap-2">
                <a
                    data-testid="resume-download"
                    className="btn btn-blue btn-sm"
                    href={`${process.env.PUBLIC_URL || ""}/Krithi_UK_Resume.pdf`}
                    download="Krithi_UK_Resume.pdf"
                    onClick={() => {
                        play("click");
                        trackEgg("egg_paper_trail");
                    }}
                >
                    <Download size={14} /> Download PDF
                </a>
                <button
                    data-testid="resume-print"
                    className="btn btn-sm"
                    onClick={() => {
                        play("click");
                        trackEgg("egg_paper_trail");
                        window.print();
                    }}
                >
                    <Printer size={14} /> Print this page
                </button>
                <span className="badge">real pdf · 1 page · updated 2025</span>
            </div>

            <article className="print-resume rounded-2xl border-2 border-ink bg-white p-5 sm:p-7">
                <header>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight text-ink">{RESUME.name}</h1>
                    <p className="mt-1 text-sm font-medium text-inksoft">{RESUME.role}</p>
                    <p className="font-mono text-[11px] text-inkfaint">{RESUME.location}</p>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-inksoft">
                        {RESUME.contact.map((c) => (
                            <a
                                key={c.label}
                                data-testid={`resume-contact-${c.label}`}
                                href={c.href}
                                target={c.href.startsWith("mailto:") ? undefined : "_blank"}
                                rel="noreferrer noopener"
                                className="underline decoration-dotted underline-offset-4 hover:text-elec hover:decoration-solid"
                            >
                                {c.value}
                            </a>
                        ))}
                    </div>
                </header>

                <Section title="professional summary">
                    <p className="text-[13px] leading-relaxed text-inksoft">{RESUME.summary}</p>
                </Section>

                <Section title="professional experience">
                    {RESUME.experience.map((e) => (
                        <div key={e.role} className="mt-1">
                            <p className="text-[13.5px] font-bold text-ink">
                                {e.role} — {e.org}
                            </p>
                            <p className="font-mono text-[11px] text-inkfaint">{e.period}</p>
                            <ul className="mt-1.5 list-disc space-y-1 pl-5">
                                {e.bullets.map((b) => (
                                    <li key={b} className="text-[13px] leading-relaxed text-inksoft">
                                        {b}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </Section>

                <Section title="research experience">
                    {RESUME.research.map((r) => (
                        <div key={r.role}>
                            <p className="text-[13.5px] font-bold text-ink">
                                {r.role} — {r.org}
                            </p>
                            <p className="font-mono text-[11px] text-inkfaint">{r.period}</p>
                            <p className="mt-1 text-[13px] text-inksoft">research areas: {r.areas}</p>
                            <p className="mt-1 text-[13px] leading-relaxed text-inksoft">{r.detail}</p>
                        </div>
                    ))}
                </Section>

                <Section title="education">
                    {RESUME.education.map((e) => (
                        <div key={e.school}>
                            <p className="text-[13.5px] font-bold text-ink">{e.degree}</p>
                            <p className="text-[13px] text-inksoft">
                                {e.school} · <span className="font-mono text-[11px]">{e.period}</span> ·{" "}
                                <span className="font-mono text-[11px]">{e.detail}</span>
                            </p>
                        </div>
                    ))}
                </Section>

                <Section title="technical skills">
                    <div className="space-y-1">
                        {RESUME.skills.map((s) => (
                            <p key={s.group} className="text-[13px] text-inksoft">
                                <span className="font-bold text-ink">{s.group}:</span> {s.items}
                            </p>
                        ))}
                    </div>
                </Section>

                <Section title="projects">
                    <ul className="list-disc space-y-1.5 pl-5">
                        {RESUME.projects.map((p) => (
                            <li key={p.name} className="text-[13px] leading-relaxed text-inksoft">
                                <span className="font-bold text-ink">{p.name}</span> — {p.blurb}
                            </li>
                        ))}
                    </ul>
                </Section>

                <Section title="certifications">
                    <ul className="list-disc space-y-1 pl-5" data-testid="resume-certifications">
                        {RESUME.certifications.map((c) => (
                            <li key={c} className="text-[13px] leading-relaxed text-inksoft">
                                {c}
                            </li>
                        ))}
                    </ul>
                </Section>
            </article>
        </div>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="mt-5 border-t border-sand pt-4">
            <h2 className="font-mono text-[11px] font-bold uppercase tracking-widest text-elec">{title}</h2>
            <div className="mt-2">{children}</div>
        </section>
    );
}
